import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../api/auth";
import { useAuth } from "../context/AuthContext";

/* ═══════════════════════════════════════════════
   HOOK RESPONSIVE
═══════════════════════════════════════════════ */
const useWindowSize = () => {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const handle = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, []);
  return width;
};

/* ═══════════════════════════════════════════════
   MOBILE TOP BAR (commun aux 3 pages)
═══════════════════════════════════════════════ */
const MobileTopBar = ({ stats }) => (
  <div style={mb.topBar}>
    {/* Logo centré */}
    <div style={mb.logo}>
      Trans<span style={{ color: "#16a34a" }}>TiGo</span>
    </div>
    {/* Stats compactes */}
    {stats && (
      <div style={mb.stats}>
        {stats.map(([v, l]) => (
          <div key={l} style={mb.statItem}>
            <div style={mb.statVal}>{v}</div>
            <div style={mb.statLabel}>{l}</div>
          </div>
        ))}
      </div>
    )}
  </div>
);

/* ═══════════════════════════════════════════════
   PANEL GAUCHE (desktop uniquement)
═══════════════════════════════════════════════ */
const PanelLeft = ({ title, sub, quote, children }) => (
  <div style={s.panel}>
    <svg style={s.panelGrid} viewBox="0 0 400 600" preserveAspectRatio="xMidYMid slice">
      <defs>
        <pattern id="g" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5"/>
        </pattern>
      </defs>
      <rect width="400" height="600" fill="url(#g)"/>
      <circle cx="80"  cy="120" r="180" fill="rgba(74,222,128,0.05)"/>
      <circle cx="320" cy="480" r="140" fill="rgba(74,222,128,0.04)"/>
      <circle cx="200" cy="300" r="60"  fill="none" stroke="rgba(74,222,128,0.12)" strokeWidth="0.5"/>
      <circle cx="200" cy="300" r="110" fill="none" stroke="rgba(74,222,128,0.07)" strokeWidth="0.5"/>
      <circle cx="200" cy="300" r="160" fill="none" stroke="rgba(74,222,128,0.04)" strokeWidth="0.5"/>
    </svg>
    <div style={s.panelContent}>
      <div style={s.panelLogo}>Trans<span style={{ color: "#4ade80" }}>TiGo</span></div>
      <div style={s.panelTextBlock}>
        <h2 style={s.panelTitle}>{title}</h2>
        <p style={s.panelSub}>{sub}</p>
      </div>
      {quote && (
        <div style={s.panelQuoteBlock}>
          <div style={s.panelQuoteLine} />
          <p style={s.panelQuote}>{quote}</p>
        </div>
      )}
      {children}
      <div style={s.panelStats}>
        {[["2 841", "Voyageurs"], ["138", "Transporteurs"], ["9 204", "Trajets"]].map(([v, l]) => (
          <div key={l}>
            <div style={s.statVal}>{v}</div>
            <div style={s.statLabel}>{l}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════
   FIELD
═══════════════════════════════════════════════ */
const Field = ({ label, icon, error, ...inputProps }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={s.label}>{label}</label>
    <div style={{ position: "relative" }}>
      <span style={s.inputIcon}>{icon}</span>
      <input style={{ ...s.input, borderColor: error ? "#f87171" : undefined, paddingLeft: 42 }} {...inputProps} />
    </div>
    {error && <div style={s.fieldError}>{error}</div>}
  </div>
);

/* ═══════════════════════════════════════════════
   LOGIN
═══════════════════════════════════════════════ */
const Login = () => {
  const navigate  = useNavigate();
  const { login } = useAuth();
  const w         = useWindowSize();
  const isMobile  = w <= 768;

  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const [showPwd,  setShowPwd]  = useState(false);

  const handleLogin = async () => {
    setLoading(true); setError("");
    try {
      const res = await axios.post(API.LOGIN, { email, password });
      login(res.data.token, res.data.refresh_token);
      navigate("/dashboard");
    } catch { setError("Email ou mot de passe incorrect."); }
    setLoading(false);
  };
  const handleKey = (e) => e.key === "Enter" && handleLogin();

  return (
    <div style={{ ...s.page, flexDirection: isMobile ? "column" : "row" }}>
      <style>{globalCSS}</style>

      {!isMobile && (
        <PanelLeft
          title="Bon retour parmi nous"
          sub="Connectez-vous pour accéder à vos billets, réservations et bien plus."
          quote="Plus de 9 000 trajets effectués en toute confiance."
        />
      )}

      <div style={{ ...s.formSide, padding: isMobile ? "0 20px 48px" : "40px 32px", background: "white" }}>
        {isMobile && (
          <MobileTopBar stats={[["2 841", "Voyageurs"], ["138", "Transporteurs"], ["9 204", "Trajets"]]} />
        )}

        <div style={{ ...s.formBox, paddingTop: isMobile ? 0 : 20 }}>
          <div style={{ ...s.formHeader, textAlign: isMobile ? "center" : "left" }}>
            <h1 style={{ ...s.formTitle, fontSize: isMobile ? 22 : 26 }}>Connexion</h1>
            <p style={s.formSub}>
              Pas encore de compte ?{" "}
              <Link to="/register" style={s.link}>S'inscrire</Link>
            </p>
          </div>

          {error && <div style={s.errorBox}><AlertIcon />{error}</div>}

          <Field label="Adresse email" icon={<MailIcon />} type="email" placeholder="vous@exemple.mg"
            value={email} onChange={e => setEmail(e.target.value)} onKeyDown={handleKey} autoComplete="email" />

          <div style={{ marginBottom: 8 }}>
            <label style={s.label}>Mot de passe</label>
            <div style={{ position: "relative" }}>
              <span style={s.inputIcon}><LockIcon /></span>
              <input style={{ ...s.input, paddingLeft: 42, paddingRight: 42 }}
                type={showPwd ? "text" : "password"} placeholder="••••••••"
                value={password} onChange={e => setPassword(e.target.value)} onKeyDown={handleKey} autoComplete="current-password" />
              <button style={s.eyeBtn} onClick={() => setShowPwd(!showPwd)} type="button">
                {showPwd ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </div>

          <div style={{ textAlign: "right", marginBottom: 28 }}>
            <Link to="/forgot" style={{ ...s.link, fontSize: 12 }}>Mot de passe oublié ?</Link>
          </div>

          <button style={s.submitBtn} onClick={handleLogin} disabled={loading} className="submit-btn">
            {loading ? <><SpinIcon /> Connexion…</> : "Se connecter"}
          </button>

          <div style={s.divider}><span style={{ ...s.dividerText, background: "white" }}>ou</span></div>

          <div style={s.altLinks}>
            <Link to="/register" style={s.altBtn}>Créer un compte voyageur</Link>
            <Link to="/register-transporter" style={s.altBtn}>Espace transporteur</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;

/* ── Icons ── */
const MailIcon   = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="1" y="3" width="14" height="10" rx="2" stroke="#9ca3af" strokeWidth="1.5"/><path d="M1 6l7 4 7-4" stroke="#9ca3af" strokeWidth="1.5"/></svg>;
const LockIcon   = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="3" y="7" width="10" height="8" rx="2" stroke="#9ca3af" strokeWidth="1.5"/><path d="M5 7V5a3 3 0 016 0v2" stroke="#9ca3af" strokeWidth="1.5"/></svg>;
const EyeIcon    = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="#9ca3af" strokeWidth="1.5"/><circle cx="8" cy="8" r="2" stroke="#9ca3af" strokeWidth="1.5"/></svg>;
const EyeOffIcon = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2 2l12 12M6.5 6.6A2 2 0 0010 10M4 4.3C2.4 5.4 1 8 1 8s2.5 5 7 5c1.4 0 2.7-.4 3.8-1M7 3.1C7.3 3 7.6 3 8 3c4.5 0 7 5 7 5s-.6 1.2-1.6 2.3" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round"/></svg>;
const AlertIcon  = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}><circle cx="8" cy="8" r="7" stroke="#f87171" strokeWidth="1.5"/><path d="M8 5v4M8 11v.5" stroke="#f87171" strokeWidth="1.5" strokeLinecap="round"/></svg>;
const SpinIcon   = () => <span style={{ display:"inline-block", width:14, height:14, border:"2px solid rgba(255,255,255,0.3)", borderTopColor:"white", borderRadius:"50%", animation:"spin 0.6s linear infinite", marginRight:8, verticalAlign:"middle" }} />;

/* ── Styles partagés ── */
const s = {
  page:         { display: "flex", minHeight: "100vh", fontFamily: "'Plus Jakarta Sans', sans-serif" },
  panel:        { width: "42%", background: "#111318", position: "relative", overflow: "hidden", flexShrink: 0 },
  panelGrid:    { position: "absolute", inset: 0, width: "100%", height: "100%" },
  panelContent: { position: "relative", zIndex: 1, display: "flex", flexDirection: "column", height: "100%", padding: "40px 44px" },
  panelLogo:    { fontSize: 22, fontWeight: 700, color: "white", letterSpacing: -0.5, marginBottom: "auto" },
  panelTextBlock:{ margin: "auto 0" },
  panelTitle:   { fontSize: 32, fontWeight: 700, color: "white", letterSpacing: -1, lineHeight: 1.2, marginBottom: 14 },
  panelSub:     { fontSize: 14, color: "rgba(255,255,255,0.45)", lineHeight: 1.7 },
  panelQuoteBlock:{ display: "flex", alignItems: "flex-start", gap: 14, margin: "40px 0" },
  panelQuoteLine: { width: 2, background: "#4ade80", borderRadius: 2, flexShrink: 0, alignSelf: "stretch", minHeight: 32 },
  panelQuote:   { fontSize: 13, color: "rgba(255,255,255,0.4)", lineHeight: 1.7, margin: 0, fontStyle: "italic" },
  panelStats:   { display: "flex", gap: 32, paddingTop: 24, borderTop: "0.5px solid rgba(255,255,255,0.08)" },
  statVal:      { fontSize: 20, fontWeight: 700, color: "white", letterSpacing: -0.5 },
  statLabel:    { fontSize: 11, color: "rgba(255,255,255,0.35)", marginTop: 2 },

  formSide:  { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "white", padding: "40px 32px" },
  formBox:   { width: "100%", maxWidth: 400 },
  formHeader:{ marginBottom: 32 },
  formTitle: { fontSize: 26, fontWeight: 700, letterSpacing: -0.8, color: "#0f1117", margin: "0 0 6px" },
  formSub:   { fontSize: 13, color: "#9ca3af", margin: 0 },
  link:      { color: "#16a34a", fontWeight: 600, textDecoration: "none" },

  errorBox:  { display: "flex", alignItems: "center", gap: 8, background: "#fff5f5", border: "0.5px solid #fecaca", borderRadius: 9, padding: "10px 14px", marginBottom: 20, fontSize: 13, color: "#dc2626" },
  fieldError:{ fontSize: 11, color: "#f87171", marginTop: 4 },

  label:     { display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6, letterSpacing: 0.2 },
  input:     { width: "100%", padding: "11px 14px", border: "0.5px solid #e0e0e0", borderRadius: 10, fontSize: 14, fontFamily: "inherit", color: "#0f1117", background: "white", outline: "none", boxSizing: "border-box", transition: "border-color 0.15s" },
  inputIcon: { position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", pointerEvents: "none" },
  eyeBtn:    { position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", alignItems: "center" },

  submitBtn: { width: "100%", padding: "13px 0", background: "#0f1117", color: "white", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 },
  divider:   { position: "relative", textAlign: "center", margin: "20px 0", borderTop: "0.5px solid #e8e8e4" },
  dividerText:{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", background: "white", padding: "0 10px", fontSize: 11, color: "#bbb" },
  altLinks:  { display: "flex", flexDirection: "column", gap: 8 },
  altBtn:    { display: "block", textAlign: "center", padding: "11px 0", border: "0.5px solid #e0e0e0", borderRadius: 10, fontSize: 13, color: "#374151", textDecoration: "none", fontWeight: 500, background: "white" },
};

/* ── Mobile top bar styles ── */
const mb = {
  topBar:    { width: "100%", background: "#111318", padding: "24px 20px 20px", marginBottom: 28, borderRadius: "0 0 18px 18px", textAlign: "center" },
  logo:      { fontSize: 22, fontWeight: 800, color: "white", letterSpacing: -0.5, marginBottom: 16 },
  stats:     { display: "flex", justifyContent: "space-around" },
  statItem:  { display: "flex", flexDirection: "column", alignItems: "center" },
  statVal:   { fontSize: 16, fontWeight: 700, color: "white", letterSpacing: -0.5 },
  statLabel: { fontSize: 10, color: "rgba(255,255,255,0.4)", marginTop: 2 },
};

const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
  @keyframes spin { to { transform: rotate(360deg); } }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  input:focus { border-color: #16a34a !important; box-shadow: 0 0 0 3px rgba(22,163,74,0.08) !important; }
  .submit-btn:hover:not(:disabled) { background: #1a1a1a !important; }
  .submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }
`;